import { X, TrendingUp, TrendingDown, Plus, Star, Target, AlertTriangle, CheckCircle, Activity, BarChart2 } from 'lucide-react';
import { Stock } from '../lib/supabase';
import { marketService } from '../lib/marketService';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect, useRef } from 'react';

interface StockDetailModalProps {
  stock: Stock | null;
  onClose: () => void;
  onAddToPortfolio?: () => void;
}

export const StockDetailModal = ({ stock, onClose, onAddToPortfolio }: StockDetailModalProps) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'chart' | 'fundamental' | 'technical'>('chart');
  const [showAddForm, setShowAddForm] = useState(false);
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (stock && chartContainerRef.current && activeTab === 'chart') {
      const container = chartContainerRef.current;
      container.innerHTML = '';

      const widgetId = `tradingview_${stock.code}_${Date.now()}`;

      const widgetDiv = document.createElement('div');
      widgetDiv.id = widgetId;
      widgetDiv.style.height = '100%';
      widgetDiv.style.width = '100%';
      container.appendChild(widgetDiv);

      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = () => {
        if (typeof (window as any).TradingView !== 'undefined') {
          new (window as any).TradingView.widget({
            container_id: widgetId,
            autosize: true,
            symbol: `IDX:${stock.code}`,
            interval: 'D',
            timezone: 'Asia/Jakarta',
            theme: 'dark',
            style: '1',
            locale: 'id',
            toolbar_bg: '#0a0e17',
            enable_publishing: false,
            allow_symbol_change: true,
            hide_top_toolbar: false,
            hide_legend: false,
            save_image: false,
            studies: ['Volume@tv-basicstudies'],
            show_popup_button: false,
            popup_width: '1000',
            popup_height: '650',
          });
        }
      };
      document.head.appendChild(script);

      return () => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      };
    }
  }, [stock, activeTab]);

  if (!stock) return null;

  const change = marketService.calculateChange(stock.price, stock.prev_close);
  const changePercent = marketService.calculateChangePercent(stock.price, stock.prev_close);
  const isPositive = change >= 0;

  const fundamentalScore = calculateFundamentalScore(stock);
  const technicalSignal = getTechnicalSignal(stock);
  const priceTarget = calculatePriceTarget(stock);

  function calculateFundamentalScore(s: Stock): number {
    let score = 50;
    if (s.pe_ratio && s.pe_ratio < 15) score += 15;
    else if (s.pe_ratio && s.pe_ratio > 30) score -= 10;
    if (s.pb_ratio && s.pb_ratio < 1.5) score += 10;
    else if (s.pb_ratio && s.pb_ratio > 3) score -= 5;
    if (s.roe && s.roe > 15) score += 15;
    else if (s.roe && s.roe < 10) score -= 10;
    if (s.der && s.der < 1) score += 10;
    else if (s.der && s.der > 2) score -= 10;
    return Math.min(100, Math.max(0, score));
  }

  function getTechnicalSignal(s: Stock): 'STRONG_BUY' | 'BUY' | 'HOLD' | 'SELL' | 'STRONG_SELL' {
    const priceVsPrev = marketService.calculateChangePercent(s.price, s.prev_close);
    const nearHigh = ((s.high - s.price) / s.high) * 100 < 2;
    const nearLow = ((s.price - s.low) / s.low) * 100 < 2;

    if (priceVsPrev > 3 && nearHigh) return 'STRONG_BUY';
    if (priceVsPrev > 1) return 'BUY';
    if (priceVsPrev < -3 && nearLow) return 'STRONG_SELL';
    if (priceVsPrev < -1) return 'SELL';
    return 'HOLD';
  }

  function calculatePriceTarget(s: Stock): { low: number; mid: number; high: number } {
    const base = s.price;
    return {
      low: Math.round(base * 0.9),
      mid: Math.round(base * 1.1),
      high: Math.round(base * 1.25)
    };
  }

  const handleAddToPortfolio = async () => {
    if (!user || !quantity || !price) return;
    setLoading(true);
    try {
      await marketService.addToPortfolio(user.id, stock.code, parseInt(quantity), parseFloat(price));
      setShowAddForm(false);
      setQuantity('');
      setPrice('');
      if (onAddToPortfolio) onAddToPortfolio();
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const signalColors = {
    STRONG_BUY: 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30',
    BUY: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    HOLD: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    SELL: 'text-red-400 bg-red-500/10 border-red-500/20',
    STRONG_SELL: 'text-red-400 bg-red-500/20 border-red-500/30'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 backdrop-blur-sm overflow-y-auto py-8 px-4">
      <div className="bg-[#0a0e17] border border-slate-800 rounded-2xl shadow-2xl w-full max-w-5xl">
        <div className="sticky top-0 bg-[#0a0e17] border-b border-slate-800 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center border border-emerald-500/30">
              <span className="font-bold text-emerald-400">{stock.code.slice(0, 2)}</span>
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <h2 className="text-xl font-bold text-white">{stock.code}</h2>
                <span className="badge-blue">{stock.sector}</span>
              </div>
              <p className="text-sm text-slate-400">{stock.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
            <X className="w-6 h-6 text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-4xl font-bold text-white font-mono">
                    Rp {marketService.formatPrice(stock.price)}
                  </div>
                  <div className={`flex items-center space-x-2 mt-1 ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                    {isPositive ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                    <span className="font-mono font-semibold">
                      {isPositive ? '+' : ''}{marketService.formatPrice(change)} ({changePercent.toFixed(2)}%)
                    </span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {user && (
                    <button
                      onClick={() => setShowAddForm(!showAddForm)}
                      className="btn-primary flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Position</span>
                    </button>
                  )}
                </div>
              </div>

              {showAddForm && (
                <div className="card p-4 space-y-3 border-emerald-500/30">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Jumlah Lot</label>
                      <input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="100" className="input-field text-sm py-2" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Harga Beli</label>
                      <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder={stock.price.toString()} className="input-field text-sm py-2" />
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={handleAddToPortfolio} disabled={loading} className="flex-1 btn-primary text-sm">{loading ? 'Adding...' : 'Confirm'}</button>
                    <button onClick={() => setShowAddForm(false)} className="btn-secondary text-sm">Cancel</button>
                  </div>
                </div>
              )}

              <div className="flex space-x-2 border-b border-slate-800">
                {['chart', 'fundamental', 'technical'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab as any)}
                    className={`px-4 py-2 font-medium capitalize transition-colors ${
                      activeTab === tab
                        ? 'text-emerald-400 border-b-2 border-emerald-400'
                        : 'text-slate-500 hover:text-slate-300'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {activeTab === 'chart' && (
                <div className="card overflow-hidden" style={{ height: '400px' }}>
                  <div ref={chartContainerRef} className="tradingview-widget-container w-full h-full"></div>
                </div>
              )}

              {activeTab === 'fundamental' && (
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'P/E Ratio', value: stock.pe_ratio?.toFixed(2) || 'N/A', desc: stock.pe_ratio && stock.pe_ratio < 15 ? 'Undervalued' : stock.pe_ratio && stock.pe_ratio > 25 ? 'Overvalued' : 'Fair', good: stock.pe_ratio && stock.pe_ratio < 15 },
                    { label: 'P/B Ratio', value: stock.pb_ratio?.toFixed(2) || 'N/A', desc: stock.pb_ratio && stock.pb_ratio < 1 ? 'Cheap' : stock.pb_ratio && stock.pb_ratio > 3 ? 'Expensive' : 'Normal', good: stock.pb_ratio && stock.pb_ratio < 1.5 },
                    { label: 'ROE', value: stock.roe ? `${stock.roe.toFixed(1)}%` : 'N/A', desc: stock.roe && stock.roe > 15 ? 'Excellent' : stock.roe && stock.roe < 10 ? 'Low' : 'Good', good: stock.roe && stock.roe > 15 },
                    { label: 'DER', value: stock.der?.toFixed(2) || 'N/A', desc: stock.der && stock.der < 1 ? 'Low Risk' : stock.der && stock.der > 2 ? 'High Risk' : 'Normal', good: stock.der && stock.der < 1 },
                  ].map((item) => (
                    <div key={item.label} className="card p-4">
                      <div className="text-xs text-slate-500 mb-1">{item.label}</div>
                      <div className="text-2xl font-bold text-white font-mono">{item.value}</div>
                      <div className={`text-xs font-medium mt-1 ${item.good ? 'text-emerald-400' : 'text-slate-400'}`}>{item.desc}</div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'technical' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-4">
                    {[
                      { label: 'Open', value: stock.open_price },
                      { label: 'High', value: stock.high, color: 'text-emerald-400' },
                      { label: 'Low', value: stock.low, color: 'text-red-400' },
                      { label: 'Prev Close', value: stock.prev_close },
                    ].map((item) => (
                      <div key={item.label} className="card p-3 text-center">
                        <div className="text-xs text-slate-500">{item.label}</div>
                        <div className={`font-mono font-semibold ${item.color || 'text-white'}`}>
                          {marketService.formatPrice(item.value)}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="card p-4">
                      <h4 className="text-sm font-medium text-slate-400 mb-3">Momentum Indicators</h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">RSI (14)</span>
                          <span className={`font-mono font-semibold ${
                            stock.rsi && stock.rsi < 30 ? 'text-emerald-400' :
                            stock.rsi && stock.rsi > 70 ? 'text-red-400' : 'text-white'
                          }`}>{stock.rsi?.toFixed(1) || '-'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Stoch RSI</span>
                          <span className={`font-mono font-semibold ${
                            stock.stoch_rsi && stock.stoch_rsi < 20 ? 'text-emerald-400' :
                            stock.stoch_rsi && stock.stoch_rsi > 80 ? 'text-red-400' : 'text-white'
                          }`}>{stock.stoch_rsi?.toFixed(1) || '-'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">MACD Signal</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                            stock.macd_signal === 'bullish' ? 'bg-emerald-500/20 text-emerald-400' :
                            stock.macd_signal === 'bearish' ? 'bg-red-500/20 text-red-400' : 'bg-slate-700 text-slate-400'
                          }`}>{stock.macd_signal?.toUpperCase() || '-'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Momentum Score</span>
                          <span className="font-mono font-semibold text-white">{stock.momentum_score?.toFixed(0) || '-'}/100</span>
                        </div>
                      </div>
                    </div>

                    <div className="card p-4">
                      <h4 className="text-sm font-medium text-slate-400 mb-3">Moving Averages</h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">MA 20</span>
                          <span className={`font-mono font-semibold ${stock.ma_20 && stock.price > stock.ma_20 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {stock.ma_20 ? marketService.formatPrice(stock.ma_20) : '-'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">MA 50</span>
                          <span className={`font-mono font-semibold ${stock.ma_50 && stock.price > stock.ma_50 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {stock.ma_50 ? marketService.formatPrice(stock.ma_50) : '-'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Support</span>
                          <span className="font-mono font-semibold text-emerald-400">{stock.support ? marketService.formatPrice(stock.support) : '-'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Resistance</span>
                          <span className="font-mono font-semibold text-red-400">{stock.resistance ? marketService.formatPrice(stock.resistance) : '-'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-slate-400">Range Hari Ini</span>
                      <span className="text-xs text-slate-500">{marketService.formatPrice(stock.low)} - {marketService.formatPrice(stock.high)}</span>
                    </div>
                    <div className="relative h-2 bg-slate-800 rounded-full">
                      <div
                        className="absolute h-2 bg-gradient-to-r from-red-500 via-amber-500 to-emerald-500 rounded-full"
                        style={{ width: '100%' }}
                      ></div>
                      <div
                        className="absolute w-3 h-3 bg-white rounded-full -top-0.5 shadow-lg"
                        style={{ left: `${((stock.price - stock.low) / (stock.high - stock.low)) * 100}%`, transform: 'translateX(-50%)' }}
                      ></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="card p-4">
                      <h4 className="text-sm font-medium text-slate-400 mb-3">Volume Analysis</h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Volume</span>
                          <span className="font-mono font-semibold text-white">{marketService.formatLargeNumber(stock.volume)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Avg Vol (20d)</span>
                          <span className="font-mono font-semibold text-white">{marketService.formatLargeNumber(stock.avg_volume_20d)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Vol Ratio</span>
                          <span className={`font-mono font-semibold ${stock.volume_ratio > 1.5 ? 'text-emerald-400' : 'text-white'}`}>
                            {stock.volume_ratio?.toFixed(2)}x
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="card p-4">
                      <h4 className="text-sm font-medium text-slate-400 mb-3">Money Flow</h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Broker Net</span>
                          <span className={`font-mono font-semibold ${stock.broker_net_buy > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {stock.broker_net_buy > 0 ? '+' : ''}{marketService.formatLargeNumber(stock.broker_net_buy)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Foreign Net</span>
                          <span className={`font-mono font-semibold ${stock.foreign_net_buy > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {stock.foreign_net_buy > 0 ? '+' : ''}{marketService.formatLargeNumber(stock.foreign_net_buy)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="card p-4 space-y-3">
                <h3 className="font-semibold text-white flex items-center space-x-2">
                  <Target className="w-4 h-4 text-emerald-400" />
                  <span>Trading Signal</span>
                </h3>
                <div className={`p-4 rounded-lg border text-center ${signalColors[technicalSignal]}`}>
                  <div className="text-2xl font-bold">{technicalSignal.replace('_', ' ')}</div>
                </div>
              </div>

              <div className="card p-4 space-y-3">
                <h3 className="font-semibold text-white flex items-center space-x-2">
                  <Activity className="w-4 h-4 text-emerald-400" />
                  <span>Health Score</span>
                </h3>
                <div className="relative pt-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-3xl font-bold text-white">{fundamentalScore}</span>
                    <span className={`text-sm font-medium ${fundamentalScore >= 70 ? 'text-emerald-400' : fundamentalScore >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                      {fundamentalScore >= 70 ? 'Excellent' : fundamentalScore >= 50 ? 'Good' : 'Poor'}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={`h-2 rounded-full ${fundamentalScore >= 70 ? 'bg-emerald-500' : fundamentalScore >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${fundamentalScore}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="card p-4 space-y-3">
                <h3 className="font-semibold text-white flex items-center space-x-2">
                  <BarChart2 className="w-4 h-4 text-emerald-400" />
                  <span>Price Target</span>
                </h3>
                <div className="space-y-2">
                  {[
                    { label: 'Bear', value: priceTarget.low, color: 'text-red-400' },
                    { label: 'Base', value: priceTarget.mid, color: 'text-amber-400' },
                    { label: 'Bull', value: priceTarget.high, color: 'text-emerald-400' },
                  ].map((target) => (
                    <div key={target.label} className="flex items-center justify-between">
                      <span className="text-slate-500 text-sm">{target.label}</span>
                      <span className={`font-mono font-semibold ${target.color}`}>
                        Rp {marketService.formatPrice(target.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Volume</span>
                  <span className="text-white font-mono">{marketService.formatLargeNumber(stock.volume)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Market Cap</span>
                  <span className="text-white font-mono">Rp {marketService.formatLargeNumber(stock.market_cap)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
